import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, ArrowRight, Sparkles, Video, Users, Inbox,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { getCampaignImages } from '../lib/utils'
import { useBrand } from '../hooks/useBrand'
import { useVideoWatchModal } from '../hooks/useVideoWatchModal'
import HomeSkeleton from '../components/HomeSkeleton'
import GetStartedChecklist from '../components/GetStartedChecklist'
import ManagedServicesBanner from '../components/ManagedServicesBanner'
import { SubmissionThumb } from '../components/SubmissionThumb'
import { VideoProductChip, VIDEO_PRODUCT_COLUMNS, type VideoProduct } from '../components/VideoProductChip'
import '../styles/home.css'
import { fmtCurrencyCents, fmtNumber } from '../lib/format'
import { PINK } from '../lib/theme'
import { styles } from './Home.styles'

/* ==================================================================
 * Home — post-redesign (Wave 1, 2026-05-02)
 *
 * Old Home was an 800+ LOC switchboard between free + paid users with
 * "trending in your category" + "top creators in your category" sections.
 *
 * The free-platform pivot (Aug 2026) removed the paid-vs-free split entirely:
 * hiring is open to every brand, so there is no "not subscribed" state to
 * render. What varies now is how far along the brand is, and the Get-Started
 * checklist carries the activation motion through all of it.
 *
 * Three states:
 *   1. No campaigns → checklist + "Create your first campaign" hero
 *   2. Has campaign(s), no videos delivered yet → checklist + cards +
 *      empty-state messaging in place of the recent-videos strip
 *   3. Full data → checklist (until complete) + activity totals +
 *      recent-videos strip + campaign cards
 *
 * The activity card shows OVERALL totals for the brand (creators hired,
 * pending applications, videos delivered) — not a since-last-visit
 * window. We don't track last_visited_at, so a time-scoped view would be
 * a misleading proxy.
 * ================================================================== */


interface CampaignSummary {
  id: string
  name: string
  product_name: string
  product_image_urls: string[]
  product_image_url: string | null
  active_creators: number
  videos_delivered: number
  total_gmv_cents: number
  status: string
}

interface RecentVideo {
  id: string
  campaign_id: string
  // Handle isn't a column on cr_video_submissions — it has to be joined from
  // roster_creators.tiktok_username. We hydrate it client-side via a
  // PostgREST nested select.
  creator: { tiktok_username: string | null } | null
  thumbnail_url: string | null
  video_url: string | null
  views: number
  gmv_cents: number
  created_at: string
  tiktok_video_url: string | null
  // Durable R2 mirror URL — same field HiredCreators.tsx uses to decide
  // between the in-app "watch" popup and a straight TikTok link-out.
  video_storage_url: string | null
}
type RecentVideoRow = RecentVideo & VideoProduct

/** One tile in the Recent videos strip — extracted so useVideoWatchModal
 *  (a hook) can be called once per video, matching HiredCreators.tsx's
 *  per-row hook usage. */
function RecentVideoTile({ video }: { video: RecentVideoRow }) {
  const { open, modal } = useVideoWatchModal(video)
  return (
    <div style={styles.videoTile}>
      <SubmissionThumb thumbnailUrl={video.thumbnail_url} tiktokUrl={video.tiktok_video_url} videoUrl={video.video_storage_url} fluid onWatchClick={open} />
      <div style={styles.videoMeta}>
        <div style={styles.videoHandle}>
          {video.creator?.tiktok_username ? `@${video.creator.tiktok_username}` : 'Creator'}
        </div>
        <div style={styles.videoStatLine}>
          {fmtNumber(video.views || 0)} views · {fmtCurrencyCents(video.gmv_cents || 0)}
        </div>
        {/* Renders nothing on a single-product campaign — this strip is a
            highlight reel, and an empty row of chips would just be noise. */}
        <VideoProductChip video={video} compact />
      </div>
      {modal}
    </div>
  )
}

interface ActivityStats {
  hires: number
  applications: number
  videos: number
  /** Campaigns that have actually gone live (posted_at IS NOT NULL). Drafts and
   *  card-held 'pending' campaigns are excluded — see the query. */
  postedCampaigns: number
}

export default function Home() {
  const { brand } = useBrand()
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [recentVideos, setRecentVideos] = useState<RecentVideoRow[]>([])
  const [stats, setStats] = useState<ActivityStats>({ hires: 0, applications: 0, videos: 0, postedCampaigns: 0 })
  const [loading, setLoading] = useState(true)

  // Grandfathered paid brands see no activation UI (dev call 2026-09-04). Read
  // straight off the tier rather than reviving the subscribed/statusOk apparatus
  // the free-platform pivot deleted from this page — all it gates now is
  // cosmetic (a banner and a checklist), never access to anything.
  const PAID_TIERS = ['starter', 'pro', 'pro_annual', 'agency']
  const grandfatheredPaid =
    !!(brand as { whop_subscription_id?: string } | null)?.whop_subscription_id ||
    PAID_TIERS.includes((brand as { plan_tier?: string } | null)?.plan_tier ?? '')

  const loadData = useCallback(async () => {
    if (!brand?.id) return
    setLoading(true)
    try {
      // Free-platform pivot (2026-08-24): Home loads for every brand — the
      // platform is free, so "not subscribed" no longer means "nothing to see".
      //
      // This brand's campaign ids — needed by both loadRecentVideos and
      // loadActivityStats. Fetched once here (in parallel with loadCampaigns'
      // own RPC, which doesn't need it) and handed to both, instead of each
      // running the same `cr_campaigns` query separately.
      const campIdsPromise = supabase
        .from('cr_campaigns').select('id').eq('brand_id', brand.id)
        .then(({ data }) => (data ?? []).map(c => c.id))
      await Promise.all([
        loadCampaigns(brand.id),
        campIdsPromise.then(campIds => loadRecentVideos(campIds)),
        campIdsPromise.then(campIds => loadActivityStats(brand.id, campIds)),
      ])
    } catch (err) {
      console.error('Home: error loading data', err)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id])

  useEffect(() => { loadData() }, [loadData])

  async function loadCampaigns(brandId: string) {
    const { data, error } = await supabase
      .rpc('get_top_campaigns_home', { p_brand_id: brandId, p_limit: 5 })
    if (error || !data) {
      setCampaigns([])
      return
    }
    setCampaigns((data as any[]).map(r => ({
      id: r.id as string,
      name: r.name as string,
      product_name: r.product_name as string,
      product_image_urls: (r.product_image_urls as string[] | null) ?? [],
      product_image_url: (r.product_image_url as string | null) ?? null,
      status: r.status as string,
      active_creators: Number(r.active_creators),
      videos_delivered: Number(r.videos_delivered),
      total_gmv_cents: Number(r.total_gmv_cents),
    })))
  }

  async function loadRecentVideos(campIds: string[]) {
    // Scoped to this brand via campIds (this brand's campaign ids — fetched
    // once in loadData and shared with loadActivityStats). Sorted by
    // submission created_at — newest first — limited to a small strip.
    if (campIds.length === 0) {
      setRecentVideos([])
      return
    }
    // cr_video_submissions has no thumbnail_url, video_url, or creator_handle
    // columns — pull what's actually there + nested-select the handle from
    // roster_creators via the creator_id FK. Thumbnail/video_url stay null
    // until we have a real TikTok video resolver for them; the UI falls
    // back to a placeholder icon.
    const { data } = await supabase
      .from('cr_video_submissions')
      .select(`id, campaign_id, views,tiktok_video_url, thumbnail_url, video_storage_url, gmv_cents, submitted_at, creator:roster_creators!cr_video_submissions_creator_id_fkey(tiktok_username), ${VIDEO_PRODUCT_COLUMNS}`)
      .in('campaign_id', campIds)
      .eq('deleted', false)
      // A rejected video no longer counts and is never shown to the brand.
      // `.or()` (not `.neq()`) so not-yet-reviewed rows (review_status IS
      // NULL) aren't also excluded by SQL's NULL semantics.
      .or('review_status.is.null,review_status.neq.rejected')
      .order('submitted_at', { ascending: false })
      .limit(6)
    // Map submitted_at → created_at on the local interface so the rest of
    // the component doesn't have to know about the column rename.
    const mapped = ((data as unknown as Array<{
      id: string; campaign_id: string; views: number; gmv_cents: number; submitted_at: string; tiktok_video_url: string | null;
      creator: { tiktok_username: string | null } | null;
      thumbnail_url: string | null;
      video_storage_url: string | null;
    } & VideoProduct>) ?? []).map(r => ({
      id: r.id,
      campaign_id: r.campaign_id,
      creator: r.creator,
      thumbnail_url: r.thumbnail_url,
      video_url: null,
      views: r.views ?? 0,
      gmv_cents: r.gmv_cents ?? 0,
      created_at: r.submitted_at,
      tiktok_video_url: r.tiktok_video_url ?? null,
      video_storage_url: r.video_storage_url ?? null,
      submitted_product_id: r.submitted_product_id ?? null,
      submitted_product_name: r.submitted_product_name ?? null,
      matched_product_id: r.matched_product_id ?? null,
      matched_product_name: r.matched_product_name ?? null,
      product_match: r.product_match ?? null,
    }))
    setRecentVideos(mapped)
  }

  async function loadActivityStats(brandId: string, campIds: string[]) {
    // Overall totals for this brand (no time window). We don't track
    // last_visited_at, so a "since last visit" proxy would be misleading.

    // Active hires — confirmed, or partial_completed (was completed, then a
    // rejected/deleted video dropped it back below videos_required — still
    // active work, not done). Covers BOTH accepted offers and accepted
    // applications, so this is "hired", not "accepted invites".
    // Deliberately excludes 'completed' — this stat tracks who you're
    // currently working with, not lifetime hires. Matches the status set
    // get_top_campaigns_home's active_creators uses below (kept in sync —
    // see 20260817030000_get_top_campaigns_home_active_status_fix.sql).
    //
    // Refunds don't change status. wallet_refund_hire_to_credit() deliberately
    // leaves status alone (20260805000000), so a hire closed out short of
    // delivery keeps status='confirmed' forever — settled_at is the actual
    // "this engagement is over" signal (20260807020000_cr_hires_settled_at.sql).
    // Every other "still active?" query in the app (CreatorRoster.tsx,
    // SendOfferModal.tsx, worker/hiring.ts) filters settled_at IS NULL; this
    // one does too.
    //
    // Counts HIRES (rows), not distinct creators — matches the campaign
    // cards below, which also count rows now (20260817040000). Both render
    // the same "active hires" label, so a creator engaged on two campaigns
    // should show as 2 there and contribute 2 here, not collapse to 1 —
    // otherwise this brand-wide total and the sum of the per-campaign cards
    // disagree the moment a creator holds hires in more than one campaign.
    // Neither of these depends on the other's result (nor on campIds), so
    // they run together instead of as two sequential round-trips.
    const [{ count: hires }, { count: apps }, { count: postedCampaigns }] = await Promise.all([
      supabase
        .from('cr_hires').select('id', { count: 'exact', head: true })
        .eq('brand_id', brandId).in('status', ['confirmed', 'partial_completed'])
        .is('settled_at', null),
      // Applications awaiting review.
      supabase
        .from('cr_applications').select('id', { count: 'exact', head: true })
        .eq('brand_id', brandId).eq('status', 'pending'),
      // Has this brand ever actually POSTED a job? Counted here rather than
      // from the campaigns list above, which comes from get_top_campaigns_home
      // — that RPC is a top-5 window and excludes archived, so it would both
      // miss a brand whose only campaign was archived and count drafts and
      // card-held 'pending' campaigns as posted.
      //
      // posted_at is the authoritative marker: the worker stamps it only on the
      // transition to active, so a draft and a 'pending' campaign are both null.
      supabase
        .from('cr_campaigns').select('id', { count: 'exact', head: true })
        .eq('brand_id', brandId).not('posted_at', 'is', null),
    ])

    // Videos delivered across all this brand's campaigns. campIds is fetched
    // once in loadData and shared with loadRecentVideos.
    let videos = 0
    if (campIds.length > 0) {
      const { count } = await supabase
        .from('cr_video_submissions').select('id', { count: 'exact', head: true })
        .in('campaign_id', campIds)
        .eq('deleted', false)
        .or('review_status.is.null,review_status.neq.rejected')
      videos = count ?? 0
    }

    setStats({
      hires: hires ?? 0,
      applications: apps ?? 0,
      videos,
      postedCampaigns: postedCampaigns ?? 0,
    })
  }

  /* ============================ Render ============================ */

  if (!brand) {
    return <div style={styles.loading}>Loading…</div>
  }

  if (loading) {
    return <HomeSkeleton />
  }

  // STATE 1 — no campaigns yet: checklist + CTA hero.
  if (campaigns.length === 0) {
    return (
      <div style={styles.page}>
        <div style={styles.headerBlock}>
          <h1 style={styles.greeting}>Welcome, {brand.brand_name}</h1>
        </div>
        {/* Grandfathered paid brands see no activation UI (dev call 2026-09-04) */}
        {!grandfatheredPaid && (
          <>
            <ManagedServicesBanner link={{ brandId: brand.id }} />
            <GetStartedChecklist
              hasCampaign={false}
              hasApplications={false}
              hasHire={false}
              onPostJob={() => navigate('/campaigns/new')}
            />
          </>
        )}
        <div style={styles.heroEmpty}>
          <div style={styles.heroIconCircle}>
            <Plus size={28} color={PINK} />
          </div>
          <h2 style={styles.heroEmptyTitle}>Create your first campaign</h2>
          <p style={styles.heroEmptyDesc}>
            One campaign = one product. Set a budget, send offers to creators, and track every
            video they deliver.
          </p>
          {/* Go straight to /campaigns/new — clicking through /campaigns
              first would just show an empty list and the same CTA again. */}
          <button onClick={() => navigate('/campaigns/new')} style={styles.ctaBtnPrimary}>
            <Plus size={16} /> Create campaign
          </button>
        </div>
      </div>
    )
  }

  // STATE 2 / 3 — has campaigns. Layout is the same; sections
  // hide/show based on whether activity exists yet.
  const hasAnyActivity = stats.hires + stats.applications + stats.videos > 0
  const hasAnyVideos = recentVideos.length > 0
  const totalActiveCreators = campaigns.reduce((s, c) => s + c.active_creators, 0)

  return (
    <div style={styles.page}>
      <div style={styles.headerBlock}>
        <h1 style={styles.greeting}>Welcome back, {brand.brand_name}</h1>
        <p style={styles.subGreeting}>{brand.category}</p>
      </div>

      {/* Grandfathered paid brands see no activation UI (dev call 2026-09-04) */}
      {!grandfatheredPaid && (
        <>
          <ManagedServicesBanner link={{ brandId: brand.id }} />
          <GetStartedChecklist
            hasCampaign={stats.postedCampaigns > 0}
            hasApplications={stats.applications > 0 || stats.hires > 0 || stats.videos > 0}
            hasHire={stats.hires > 0 || stats.videos > 0}
            onPostJob={() => navigate('/campaigns/new')}
          />
        </>
      )}

      {/* Activity totals — only when there's something to show. Otherwise
          we'd render "0 active hires · 0 applications" which feels like
          noise. */}
      {hasAnyActivity && (
        <div style={styles.sinceCard}>
          <div style={styles.sinceLabel}>Your activity</div>
          <div style={styles.sinceRow}>
            <SinceStat icon={<Users size={16} />} value={stats.hires} label="active hires" />
            <SinceStat icon={<Inbox size={16} />} value={stats.applications} label="pending applications" />
            <SinceStat icon={<Video size={16} />} value={stats.videos} label="videos delivered" />
          </div>
        </div>
      )}

      {/* Recent videos strip — only when there are videos to show. */}
      {hasAnyVideos && (
        <div>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Recent videos</h2>
            {/* Default the "see all" link to the first campaign's video tab.
                Multi-campaign brands can still navigate per-campaign from
                the cards below. */}
            <button onClick={() => navigate(`/videos`)} style={styles.sectionAction}>
              See all <ArrowRight size={12} />
            </button>
          </div>
          <div className="home-video-strip" style={styles.videoStrip}>
            {recentVideos.map(v => (
              <RecentVideoTile key={v.id} video={v} />
            ))}
          </div>
        </div>
      )}

      {/* Campaign cards (1-3 typically). Replaces the spend-bar overview
          with creator/video/GMV counts that match the new IA. */}
      <div>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            {campaigns.length === 1 ? 'Your campaign' : 'Your campaigns'}
          </h2>
          <button onClick={() => navigate('/campaigns')} style={styles.sectionAction}>
            Manage <ArrowRight size={12} />
          </button>
        </div>
        <div style={styles.campaignGrid}>
          {campaigns.map(c => (
            <HomeCampaignCard
              key={c.id}
              campaign={c}
              // Drafts resume the wizard; everything else opens the read-only
              // detail view (locked fields + creator-app preview) — same
              // behavior as the /campaigns list cards.
              onClick={() => navigate(c.status === 'draft' ? `/campaigns/new?draft=${c.id}` : `/campaigns/${c.id}/view`)}
            />
          ))}
        </div>

        {/* State 3 nudge: brand has campaign(s) but zero creators yet.
            Surface a clear next step without pushing them off Home. */}
        {totalActiveCreators === 0 && (
          <div style={styles.nudgeCard}>
            <Users size={18} color={PINK} />
            <span style={{ flex: 1 }}>Time to find creators for your campaign.</span>
            <button onClick={() => navigate('/roster')} style={styles.nudgeBtn}>
              Browse creators <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/* ============================ Sub-components ============================ */

function SinceStat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div style={styles.sinceStat}>
      <div style={{ color: PINK, display: 'flex', alignItems: 'center' }}>{icon}</div>
      <div style={styles.sinceValue}>{value}</div>
      <div style={styles.sinceStatLabel}>{label}</div>
    </div>
  )
}

function HomeCampaignCard({ campaign: c, onClick }: { campaign: CampaignSummary; onClick: () => void }) {
  return (
    <div style={styles.campaignCard} onClick={onClick}>
      <div style={styles.campaignThumbWrap}>
        {getCampaignImages(c)[0] ? (
          <img src={getCampaignImages(c)[0]} alt={c.product_name} style={styles.campaignThumb} />
        ) : (
          <div style={styles.campaignThumbFallback}>
            <Sparkles size={20} color="#55556a" />
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.campaignName}>{c.name}</div>
        <div style={styles.campaignProduct}>{c.product_name}</div>
        <div style={styles.campaignStats}>
          <span><strong>{c.active_creators}</strong> active hires</span>
          <span>·</span>
          <span><strong>{c.videos_delivered}</strong> videos</span>
          <span>·</span>
          <span><strong>{fmtCurrencyCents(c.total_gmv_cents)}</strong> GMV</span>
        </div>
      </div>
      <ArrowRight size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
    </div>
  )
}

/* ============================ Styles ============================ */
